/**
 * Compilación del layout a un modelo espacial (componente 7 de la auditoría del v6, adaptado):
 * convierte elementos y vías en estaciones con bocas de acceso, un grafo de circulación, la matriz
 * de distancias por ruta y las rutas que dibuja la escena. Mover un elevador o una vía recalcula
 * distancias y tiempos de traslado. Si no existe ruta, la validación bloquea (nunca línea recta).
 */

import type { Catalogo, Elemento, Espacio, Punto, TipoBahia } from '../dominio/espacio';
import type { Observacion } from '../dominio/observacion';
import type { TipoBahiaActiva, TipoDeUnidad, TipoEstacion } from '../dominio/proceso';
import {
  ALCANCE_BOCA_M,
  bocasDeElemento,
  giroHaciaVia,
  girosDeVecinos,
  MARCADOR_MAX_M,
  type BocaEnPlanta,
} from './bocas';
import {
  caminosMinimos,
  construirGrafo,
  reconstruirCamino,
  tramosVehiculares,
  type GrafoCirculacion,
} from './circulacion';
import { distancia } from './geometria';
import { extremosSueltos, type ExtremoSuelto } from './vias';

export interface EstacionEnPlanta {
  /** Código visible: B01, R01, P01, L01, E01, ING, SAL. */
  id: string;
  tipo: TipoEstacion;
  elementoId: string;
  centro: Punto;
  rotacion: number;
  bocaEntrada: Punto | null;
  bocaSalida: Punto | null;
  /** Bocas en el borde del elemento (paso 1.4): el plano las dibuja verdes o rojas. */
  bocas: BocaEnPlanta[];
  capacidad: number | null;
  /** Tipos de unidad que admite; `null` = todos. */
  compatibles: string[] | null;
  especializada: boolean;
  /** Tipo de elevador, solo en las bahías que simulan (paso 1.6: tiempos propios, técnico fijo). */
  tipoBahia?: TipoBahiaActiva;
  sigueTurno: boolean;
}

/**
 * Servicios que admite un tipo de bahía, según lo que declara cada servicio. Un tipo no
 * especializado que admiten todos queda abierto (`null`), como el elevador mixto.
 */
export function compatiblesDeBahia(
  tipo: TipoBahia,
  tipos: readonly TipoDeUnidad[],
  especializada: boolean,
): string[] | null {
  if (tipo === 'inactivo') return [];
  const admiten = tipos.filter((t) => t.bahias.includes(tipo)).map((t) => t.id);
  return !especializada && admiten.length === tipos.length ? null : admiten;
}

/**
 * Punto de una ruta dibujada, con el metro del recorrido por el eje de las vías que le corresponde
 * (`s`): la escena avanza por esa distancia, la misma con que el motor reserva los tramos.
 */
export interface PuntoDeRuta extends Punto {
  s: number;
}

export interface ModeloEspacial {
  estaciones: EstacionEnPlanta[];
  grafo: GrafoCirculacion;
  /** Metros por la ruta más corta (índices de `estaciones`); `null` = sin ruta. */
  distancias: (number | null)[][];
  /**
   * Polilínea de cada ruta que dibuja la escena, con clave «origen>destino»; en las vías de doble
   * sentido, por el carril de la derecha (paso 1.6, avance 3).
   */
  rutas: Record<string, PuntoDeRuta[]>;
  /** Nodos del grafo por los que pasa cada ruta (por el eje de las vías), con la misma clave. */
  caminos: Record<string, number[]>;
  /**
   * Cola del ingreso (paso 1.6, avance 3): hacia dónde crece (hacia la calle) y cuántos autos caben
   * en la vía de acceso, dentro del espacio, a un tramo de 6 m cada uno. Los demás esperan «en la
   * calle». `null` si no hay ingreso.
   */
  colaIngreso: { direccion: Punto; capacidad: number } | null;
  /** Elementos que quedaron fuera del marco del espacio (no se simulan). */
  fueraDelEspacio: string[];
  /** Extremos de vías vehiculares que no se unen a nada (paso 1.4). */
  extremosSueltos: ExtremoSuelto[];
  observaciones: Observacion[];
}

const PREFIJO: Record<TipoEstacion, string> = {
  bahia: 'B',
  recepcion: 'R',
  patio: 'P',
  control: 'BC',
  lavado: 'L',
  entrega: 'E',
  ingreso: 'ING',
  salida: 'SAL',
};

export const NOMBRE_ESTACION: Record<TipoEstacion, string> = {
  bahia: 'Elevador',
  recepcion: 'Cajón de recepción',
  patio: 'Cajón de patio',
  control: 'Bahía de espera de control',
  lavado: 'Puesto de lavado',
  entrega: 'Cajón de entrega',
  ingreso: 'Ingreso vehicular',
  salida: 'Salida vehicular',
};

export function dentroDelMarco(e: Elemento, marco: Espacio['marco']): boolean {
  return (
    Math.abs(e.posicion.x - marco.x) <= marco.ancho / 2 + 1e-6 &&
    Math.abs(e.posicion.z - marco.z) <= marco.fondo / 2 + 1e-6
  );
}

export function compilarEspacio(espacio: Espacio, catalogo: Catalogo): ModeloEspacial {
  const observaciones: Observacion[] = [];
  const tramos = tramosVehiculares(espacio.layout.vias);
  const estaciones: EstacionEnPlanta[] = [];
  const fueraDelEspacio: string[] = [];
  const contador: Partial<Record<TipoEstacion, number>> = {};

  for (const e of espacio.layout.elementos) {
    const item = catalogo[e.catalogoId];
    const rol = item?.rolOperativo;
    if (!item) continue;
    if (!dentroDelMarco(e, espacio.marco)) {
      fueraDelEspacio.push(e.id);
      continue;
    }
    // Solo las estaciones con boca vehicular reciben autos.
    const boca = item.boca;
    if (!rol || !boca || boca.circulacion !== 'vehicular') continue;
    const tipoBahia = e.propiedades?.tipoBahia ?? 'mixto';
    const variante = rol.variantes ? rol.variantes[tipoBahia] : undefined;
    if (variante && !variante.activa) continue;

    const n = (contador[rol.estacion] ?? 0) + 1;
    if ((rol.estacion === 'ingreso' || rol.estacion === 'salida') && n > 1) continue; // se advierte en validación
    contador[rol.estacion] = n;
    const id =
      boca.forma === 'marcador'
        ? PREFIJO[rol.estacion]
        : `${PREFIJO[rol.estacion]}${String(n).padStart(2, '0')}`;
    const bocas = bocasDeElemento(e, boca, tramos);
    let centro = e.posicion;
    let bocaEntrada: Punto | null = null;
    let bocaSalida: Punto | null = null;
    const etiqueta =
      `${NOMBRE_ESTACION[rol.estacion]} ${boca.forma === 'marcador' ? '' : id}`.trim();

    if (boca.forma === 'marcador') {
      const enVia = bocas[0]!.via;
      if (!enVia) {
        observaciones.push({
          regla: 'marcador-lejos-de-via',
          nivel: 'bloquea',
          que: `${NOMBRE_ESTACION[rol.estacion]} lejos de una vía vehicular`,
          donde: `${NOMBRE_ESTACION[rol.estacion]} (a más de ${MARCADOR_MAX_M} m de cualquier vía)`,
          porque: 'Los autos no pueden entrar ni salir del taller si el punto no toca una vía.',
          queHacer: `Acércalo a una vía (a menos de ${MARCADOR_MAX_M} m) o dibuja una vía que pase por él.`,
          elementos: [e.id],
        });
      } else {
        centro = enVia;
        bocaEntrada = enVia;
        bocaSalida = enVia;
      }
    } else {
      bocaEntrada = bocas[0]!.via;
      bocaSalida = bocas.find((b) => b.rol === 'salida')?.via ?? bocaEntrada;
      if (!bocaEntrada) {
        const lado = boca.forma === 'delante' ? 'delante' : 'detrás';
        const giro = giroHaciaVia(e, boca, tramos, girosDeVecinos(e, espacio.layout.elementos));
        observaciones.push({
          regla: 'estacion-sin-acceso',
          nivel: 'bloquea',
          que: `${etiqueta} sin acceso`,
          donde: etiqueta,
          porque: `Su boca de acceso (${lado}) no llega a ninguna vía vehicular en ${ALCANCE_BOCA_M} m, así que ningún auto puede entrar.`,
          queHacer:
            giro === null
              ? `No hay una vía a menos de ${ALCANCE_BOCA_M} m en ninguna dirección: acércalo a una vía o dibuja una vía ${lado} del elemento.`
              : 'Usa «Orientar hacia la vía»: lo gira para que su boca mire a la vía más cercana. También puedes acercarlo o dibujar una vía.',
          elementos: [e.id],
          ...(giro === null
            ? {}
            : {
                accion: { tipo: 'orientar-hacia-via' as const, elementoId: e.id, rotacion: giro },
              }),
        });
      }
    }
    estaciones.push({
      id,
      tipo: rol.estacion,
      elementoId: e.id,
      centro,
      rotacion: e.rotacion,
      bocaEntrada,
      bocaSalida,
      bocas,
      capacidad: rol.capacidad,
      compatibles: variante
        ? compatiblesDeBahia(
            tipoBahia,
            espacio.modelo.proceso.tiposDeUnidad,
            variante.especializada,
          )
        : null,
      especializada: variante ? variante.especializada : false,
      ...(rol.estacion === 'bahia' && tipoBahia !== 'inactivo' ? { tipoBahia } : {}),
      sigueTurno: rol.sigueTurno,
    });
  }

  const anclas: Punto[] = [];
  for (const s of estaciones) {
    if (s.bocaEntrada) anclas.push(s.bocaEntrada);
    if (s.bocaSalida && s.bocaSalida !== s.bocaEntrada) anclas.push(s.bocaSalida);
  }
  const grafo = construirGrafo(espacio.layout.vias, anclas);
  const nodoEntrada = estaciones.map((s) =>
    s.bocaEntrada ? grafo.buscar(s.bocaEntrada) : undefined,
  );
  const nodoSalida = estaciones.map((s) => (s.bocaSalida ? grafo.buscar(s.bocaSalida) : undefined));

  const n = estaciones.length;
  const distancias: (number | null)[][] = Array.from({ length: n }, () =>
    new Array<number | null>(n).fill(null),
  );
  const rutas: Record<string, PuntoDeRuta[]> = {};
  const caminos: Record<string, number[]> = {};
  for (let i = 0; i < n; i++) {
    const a = estaciones[i]!;
    distancias[i]![i] = 0;
    const origen = nodoSalida[i];
    if (origen === undefined || !a.bocaSalida) continue;
    const { dist, previo } = caminosMinimos(grafo, origen);
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      const b = estaciones[j]!;
      const destino = nodoEntrada[j];
      if (destino === undefined || !b.bocaEntrada || !Number.isFinite(dist[destino]!)) continue;
      distancias[i]![j] =
        distancia(a.centro, a.bocaSalida) + dist[destino]! + distancia(b.bocaEntrada, b.centro);
      const camino = reconstruirCamino(previo, destino);
      caminos[`${a.id}>${b.id}`] = camino;
      rutas[`${a.id}>${b.id}`] = depurar(
        trazoPorCarril(grafo, a.centro, camino, b.centro, distancias[i]![j]!),
      );
    }
  }
  return {
    estaciones,
    grafo,
    distancias,
    rutas,
    caminos,
    colaIngreso: colaDelIngreso(espacio, estaciones, grafo, caminos),
    fueraDelEspacio,
    extremosSueltos: extremosSueltos(espacio, anclas),
    observaciones,
  };
}

/** Largo de cada lugar de la cola del ingreso y de cada tramo de circulación (ADR 0012). */
export const LARGO_TRAMO_M = 6;

/**
 * Polilínea de una ruta por los nodos del grafo: en cada arista de una vía de doble sentido, corrida a
 * la derecha del sentido de marcha (un carril por sentido, ADR 0012), para que dos autos que se
 * cruzan no se dibujen uno encima del otro. Cada punto lleva su metro del recorrido por el eje (`s`).
 * En la boca de salida y en la de llegada, el auto pasa del eje a su carril (o al revés) en el mismo
 * metro: así gira con el centro sobre el eje y su cola no sale de la calzada. El cambio de carril en
 * una esquina se reparte en un tramo corto alrededor del nodo.
 */
function trazoPorCarril(
  grafo: GrafoCirculacion,
  desde: Punto,
  camino: readonly number[],
  hasta: Punto,
  largo: number,
): PuntoDeRuta[] {
  const nodo = (k: number): Punto => grafo.nodos[camino[k]!]!;
  const ultimo = camino.length - 1;
  const s0 = distancia(desde, nodo(0));
  const puntos: PuntoDeRuta[] = [
    { x: desde.x, z: desde.z, s: 0 },
    { x: nodo(0).x, z: nodo(0).z, s: s0 },
  ];
  let s = s0;
  for (let k = 0; k < ultimo; k++) {
    const A = nodo(k);
    const B = nodo(k + 1);
    const c = grafo.nodos[camino[k]!]!.adyacentes.find((e) => e.a === camino[k + 1])?.carril ?? 0;
    const e = distancia(A, B);
    const L = e || 1;
    // Derecha del sentido de marcha, mirando la planta desde arriba (norte hacia −z).
    const dx = (-(B.z - A.z) / L) * c;
    const dz = ((B.x - A.x) / L) * c;
    const inicio = { x: A.x + dx, z: A.z + dz, s };
    const previo = puntos[puntos.length - 1]!;
    const salto = k === 0 ? 0 : distancia(previo, inicio);
    if (salto > 0.01) {
      // Esquina: el punto anterior se adelanta y este se atrasa media vuelta de la esquina.
      const medio = Math.min(salto / 2, e / 2);
      previo.s = Math.max(previo.s - medio, puntos[puntos.length - 2]?.s ?? 0);
      inicio.s = s + medio;
    }
    puntos.push(inicio);
    s += e;
    puntos.push({ x: B.x + dx, z: B.z + dz, s });
  }
  puntos.push({ x: nodo(ultimo).x, z: nodo(ultimo).z, s }, { x: hasta.x, z: hasta.z, s: largo });
  return puntos;
}

/**
 * Cola del ingreso: crece en sentido contrario al primer tramo por el que sale el primer auto (hacia
 * la calle); caben tantos autos como lugares de 6 m entre el ingreso y el borde del espacio.
 */
function colaDelIngreso(
  espacio: Espacio,
  estaciones: readonly EstacionEnPlanta[],
  grafo: GrafoCirculacion,
  caminos: Readonly<Record<string, number[]>>,
): { direccion: Punto; capacidad: number } | null {
  const ing = estaciones.find((s) => s.tipo === 'ingreso');
  if (!ing) return null;
  let direccion: Punto = { x: 0, z: 1 };
  for (const s of estaciones) {
    const c = caminos[`${ing.id}>${s.id}`];
    if (!c || c.length < 2) continue;
    const A = grafo.nodos[c[0]!]!;
    const B = grafo.nodos[c[1]!]!;
    const L = Math.hypot(B.x - A.x, B.z - A.z);
    if (L < 1e-6) continue;
    direccion = { x: -(B.x - A.x) / L, z: -(B.z - A.z) / L };
    break;
  }
  // Distancia del ingreso al borde del marco (rectángulo alineado con los ejes) en esa dirección.
  const m = espacio.marco;
  const limite = (p: number, d: number, centro: number, medio: number) =>
    Math.abs(d) < 1e-9 ? Infinity : ((d > 0 ? centro + medio : centro - medio) - p) / d;
  const hastaBorde = Math.max(
    0,
    Math.min(
      limite(ing.centro.x, direccion.x, m.x, m.ancho / 2),
      limite(ing.centro.z, direccion.z, m.z, m.fondo / 2),
    ),
  );
  return { direccion, capacidad: Math.floor(hastaBorde / LARGO_TRAMO_M + 1e-9) };
}

/** Quita puntos repetidos consecutivos. */
function depurar<P extends Punto>(puntos: P[]): P[] {
  const r: P[] = [];
  for (const q of puntos) {
    const ultimo = r[r.length - 1];
    if (!ultimo || distancia(ultimo, q) > 0.01) r.push(q);
  }
  return r;
}
